import type {
  CompetitionOrganizerEntry,
  CompetitionOrganizerEntrantMember,
  CompetitionOrganizerSummary,
} from '@tark/shared/lib';

export type OrganizerEntryIntent =
  | { kind: 'decline' | 'withdraw' | 'replace'; entry: CompetitionOrganizerEntry }
  | { kind: 'merge'; entry: CompetitionOrganizerEntry; source: CompetitionOrganizerEntrantMember }
  | { kind: 'lock'; competition: CompetitionOrganizerSummary; competitionVersion: number };

export type OrganizerDialogSubmission = {
  reason: string;
  outgoingEntrantPublicId?: string;
  targetEntrantPublicId?: string;
};

export type OrganizerFilters = {
  search: string;
  divisionPublicId: string;
  status: string;
};

const STATUS_PRIORITY: Record<string, number> = {
  PENDING: 0,
  RESERVE: 1,
  APPROVED: 2,
  DECLINED: 3,
  WITHDRAWN: 4,
};

export function filterOrganizerEntries(
  entries: CompetitionOrganizerEntry[],
  filters: OrganizerFilters
): CompetitionOrganizerEntry[] {
  const query = filters.search.trim().toLowerCase();

  return [...entries]
    .filter((entry) => !filters.divisionPublicId || entry.divisionPublicId === filters.divisionPublicId)
    .filter((entry) => !filters.status || entry.status === filters.status)
    .filter((entry) => {
      if (!query) return true;
      return [
        entry.divisionName,
        entry.formatCode,
        ...entry.members.flatMap((member) => [member.displayName, member.levelLabel, member.homeArea]),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    })
    .sort((left, right) => {
      const priority = (STATUS_PRIORITY[left.status] ?? 99) - (STATUS_PRIORITY[right.status] ?? 99);
      if (priority) return priority;
      if (left.status === 'RESERVE' && right.status === 'RESERVE') {
        const reserveOrder = (left.reservePosition ?? Number.MAX_SAFE_INTEGER) - (right.reservePosition ?? Number.MAX_SAFE_INTEGER);
        if (reserveOrder) return reserveOrder;
      }
      return new Date(left.requestedAt).getTime() - new Date(right.requestedAt).getTime();
    });
}

export function formatCompetitionCode(value?: string | null): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatCompetitionDate(value?: string | null, timeZone?: string | null): string {
  if (!value) return 'Not set';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone || undefined,
    }).format(new Date(value));
  } catch {
    return 'Date unavailable';
  }
}

export function organizerStatusClass(status: string): string {
  switch (status) {
    case 'APPROVED':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700';
    case 'PENDING':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-700';
    case 'RESERVE':
      return 'border-sky-500/30 bg-sky-500/10 text-sky-700';
    case 'DECLINED':
    case 'WITHDRAWN':
      return 'border-border bg-surface-elevated text-text-muted';
    default:
      return 'border-border bg-surface text-text-muted';
  }
}

export function uniqueLinkedEntrants(entries: CompetitionOrganizerEntry[]) {
  const entrants = new Map<string, CompetitionOrganizerEntrantMember>();
  entries.forEach((entry) => {
    entry.members.forEach((member) => {
      if (member.linkedAccount) entrants.set(member.entrantPublicId, member);
    });
  });
  return [...entrants.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function organizerLockHint(
  competition: CompetitionOrganizerSummary,
  entries: CompetitionOrganizerEntry[]
): { ready: boolean; text: string } {
  if (competition.entrantsLockedAt) {
    return { ready: false, text: 'Entrants are locked.' };
  }
  if (competition.status !== 'OPEN') {
    return { ready: false, text: 'The competition must be open before entrants can be locked.' };
  }
  if (competition.registrationClosesAt && new Date(competition.registrationClosesAt).getTime() > Date.now()) {
    return { ready: false, text: 'Locking opens after registration closes.' };
  }
  const pending = entries.filter((entry) => entry.status === 'PENDING').length;
  if (pending > 0) {
    return { ready: false, text: `Review ${pending} pending ${pending === 1 ? 'entry' : 'entries'} first.` };
  }
  return { ready: true, text: 'Ready for the final division and capacity checks.' };
}
